import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useCallHistory } from '@/hooks/useCallHistory';
import type { CallLogRow } from '@/services/calls-api';

function fmt(iso: string): string {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function durationLabel(sec: number | null): string {
  if (!sec || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function lineFor(
  row: CallLogRow,
  meId: string | null,
  t: TFunction,
): {
  name: string;
  icon: string;
  color: string;
  subline: string;
} {
  const outgoing = row.callerId === meId;
  const peerName = outgoing ? row.calleeName : row.callerName;
  const name = peerName ?? 'Unknown';

  const isVideo = row.callType === 'VIDEO';
  const baseIcon = isVideo ? 'videocam' : 'call';

  let subline: string;
  let color: string;
  let icon: string;

  if (row.status === 'MISSED' || (row.status === 'NO_ANSWER' && !outgoing)) {
    subline = t('call_history.missed');
    color = 'var(--status-alert, #FF3B30)';
    icon = outgoing ? 'call_missed_outgoing' : 'call_missed';
  } else if (row.status === 'REJECTED') {
    subline = outgoing ? t('call_history.rejected_out') : t('call_history.rejected_in');
    color = 'var(--status-alert, #FF3B30)';
    icon = 'call_end';
  } else if (row.status === 'COMPLETED') {
    subline = t('call_history.duration', { d: durationLabel(row.durationSec) });
    color = 'var(--status-success, #34C759)';
    icon = outgoing ? 'call_made' : 'call_received';
  } else {
    subline = t(`call_history.status_${row.status.toLowerCase()}`, { defaultValue: row.status });
    color = 'var(--text-tertiary)';
    icon = baseIcon;
  }

  return { name, icon, color, subline };
}

export function CallHistoryList() {
  const { t } = useTranslation();
  const { data: rows = [], isLoading } = useCallHistory();
  const user = useAuthStore((s) => s.user);

  if (isLoading) return <div className="call-history-empty" aria-busy="true" />;
  if (rows.length === 0) {
    return (
      <div className="call-history-empty">
        <span className="material-symbols-outlined">call</span>
        {t('call_history.empty')}
      </div>
    );
  }

  return (
    <div className="call-history-list">
      {rows.map((row) => {
        const line = lineFor(row, user?.id ?? null, t);
        return (
          <div key={row.id} className="call-history-row">
            <span className="material-symbols-outlined call-history-icon" style={{ color: line.color }}>
              {line.icon}
            </span>
            <div className="call-history-info">
              <div className="call-history-name">{line.name}</div>
              <div className="call-history-sub">{line.subline}</div>
            </div>
            <div className="call-history-time">{fmt(row.startedAt)}</div>
          </div>
        );
      })}
    </div>
  );
}
